import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CardContent,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { message } from "antd";

const UpdateStudent = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    name: "",
    phone_number: "",
    alt_phone_number: "",
    class_id: "",
    branch_id: "",
    section_id: "",
    gender: "",
  });

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";
  const token = localStorage.getItem("token");
  React.useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://api.clever-school.com/api/students/${id}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (isMounted) {
          const userData = response.data;
          setFormData({
            name: userData.name || "",
            section_id: userData.section_id || "",
            branch_id: userData.branch_id || "",
            class_id: userData.class_id || "",
            phone_number: userData.phone_number || "",
            alt_phone_number: userData.alt_phone_number || "",
            gender: userData.gender || "",
          });
        }
      } catch (error) {
        console.error("Error fetching student data:", error);
        message.error(t("error.fetchingData"));
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [id, t, token]);

  const handleChange = (name) => (e) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await axios.put(
        `https://api.clever-school.com/api/students/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("studentUpdated"));
      navigate("/GetStudent");
    } catch (error) {
      console.error("Update error:", error);
      message.error(t("studentUpdateFailed"));
    }
  };
  const [userRole, setUserRole] = React.useState("");
  // eslint-disable-next-line no-unused-vars
  const [userBranch, setUserBranch] = React.useState("");

  React.useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        setUserBranch(user.branch_id);

        // Fixed: Update formData instead of non-existent setBranch_id
        if (user.role !== "superadmin") {
          setFormData((prev) => ({
            ...prev,
            branch_id: user.branch_id.toString(),
          }));
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);
  const formFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "none",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const formSelect = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": {
      border: "none",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("updateStudentInfo")}
          </Typography>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("name")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={handleChange("name")}
                  placeholder={t("enterName")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("gender")}
                </Typography>
                <Select
                  fullWidth
                  value={formData.gender}
                  onChange={handleChange("gender")}
                  variant="outlined"
                  displayEmpty
                  required
                  sx={formSelect}
                >
                  <MenuItem value="" disabled>
                    {t("selectGender")}
                  </MenuItem>
                  <MenuItem value="M">{t("male")}</MenuItem>
                  <MenuItem value="F">{t("female")}</MenuItem>
                </Select>
              </CardContent>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("phoneNumber")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.phone_number}
                  onChange={handleChange("phone_number")}
                  placeholder={t("enterPhoneNumber")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("altPhoneNumber")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.alt_phone_number}
                  onChange={handleChange("alt_phone_number")}
                  placeholder={t("enterAltPhoneNumber")}
                  variant="outlined"
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* TODO: Academic Information */}
            <Grid item xs={12} md={userRole === "superadmin" ? 4 : 6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("class")}
                </Typography>
                <Select
                  fullWidth
                  value={formData.class_id}
                  onChange={handleChange("class_id")}
                  variant="outlined"
                  displayEmpty
                  required
                  sx={formSelect}
                >
                  <MenuItem value="" disabled>
                    {t("selectClass")}
                  </MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </CardContent>
            </Grid>

            <Grid item xs={12} md={userRole === "superadmin" ? 4 : 6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("section")}
                </Typography>
                <Select
                  fullWidth
                  value={formData.section_id}
                  onChange={handleChange("section_id")}
                  variant="outlined"
                  displayEmpty
                  required
                  sx={formSelect}
                >
                  <MenuItem value="" disabled>
                    {t("selectSection")}
                  </MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                    <MenuItem key={num} value={num}>
                      {String.fromCharCode(64 + num)}
                    </MenuItem>
                  ))}
                </Select>
              </CardContent>
            </Grid>

            {userRole === "superadmin" && (
              <Grid item xs={12} md={4}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("branch")}
                  </Typography>
                  <Select
                    fullWidth
                    value={formData.branch_id}
                    onChange={handleChange("branch_id")}
                    variant="outlined"
                    displayEmpty
                    required
                    sx={formSelect}
                  >
                    <MenuItem value="" disabled>
                      {t("selectBranch")}
                    </MenuItem>
                    <MenuItem value={1}>{t("branch1.1")}</MenuItem>
                    <MenuItem value={2}>{t("branch1.2")}</MenuItem>
                    <MenuItem value={3}>{t("branch1.3")}</MenuItem>
                  </Select>
                </CardContent>
              </Grid>
            )}
            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/GetStudent")}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    px: 6,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  {t("update")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default UpdateStudent;
